/**
 * Variant Selection Guard (v3)
 * ------------------------------------------------------------------
 * Fix vs v2: dropdown-style variant pickers put their radio inputs
 * inside <x-popover> elements, and on this theme those popovers can
 * sit outside the <form> in the light DOM tree. `form.elements` does
 * not reliably enumerate inputs associated only via the `form="..."`
 * attribute across that boundary, so v2's option lookups silently
 * found nothing. This version finds inputs directly via
 * `input[data-option-position][form="<form-id>"]` queried from
 * `document`, which works regardless of where they live in the tree.
 */
(function () {
  const touchedPositionsByFormId = new Map();

  function getFormId(form) {
    return form.getAttribute('id');
  }

  function getTouched(formId) {
    if (!touchedPositionsByFormId.has(formId)) {
      touchedPositionsByFormId.set(formId, new Set());
    }
    return touchedPositionsByFormId.get(formId);
  }

  function getOptionInputs(formId) {
    return Array.from(
      document.querySelectorAll(`input[data-option-position][form="${formId}"]`)
    );
  }

  function getOptionPositions(formId) {
    return Array.from(
      new Set(getOptionInputs(formId).map((el) => el.getAttribute('data-option-position')))
    );
  }

  function getUntouchedPositions(formId) {
    const touched = getTouched(formId);
    return getOptionPositions(formId).filter((position) => !touched.has(position));
  }

  function uncheckUntouchedOptions(formId) {
    const untouched = getUntouchedPositions(formId);
    if (untouched.length === 0) return;

    getOptionInputs(formId).forEach((input) => {
      if (untouched.includes(input.getAttribute('data-option-position'))) {
        input.checked = false;
      }
    });

    // Reset the dropdown button's visible label back to "Select" for untouched options
    untouched.forEach((position) => {
      const selectedValueSpan = document.querySelector(
        `[aria-controls^="popover-variant-dropdown-"][aria-controls$="-${position}"] [id$="-selected-value"]`
      );
      if (selectedValueSpan) {
        selectedValueSpan.textContent = 'Select';
      }
    });
  }

  function clearHiddenIdIfIncomplete(form, formId) {
    if (getUntouchedPositions(formId).length > 0) {
      const idInput = form.elements.namedItem('id');
      if (idInput) {
        idInput.value = '';
      }
    }
  }

  function getOrCreateErrorElement(form) {
    let errorEl = form.querySelector('.variant-guard-error');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'variant-guard-error';
      errorEl.setAttribute('role', 'alert');
      errorEl.style.color = '#BF1515';
      errorEl.style.fontSize = '0.875rem';
      errorEl.style.marginTop = '0.5rem';
      errorEl.hidden = true;
      const buyButtons = form.querySelector('buy-buttons');
      if (buyButtons) {
        buyButtons.insertAdjacentElement('afterend', errorEl);
      } else {
        form.appendChild(errorEl);
      }
    }
    return errorEl;
  }

  function showError(form) {
    const errorEl = getOrCreateErrorElement(form);
    errorEl.textContent = 'Please select all options before adding to cart.';
    errorEl.hidden = false;
  }

  function hideError(form) {
    const errorEl = form.querySelector('.variant-guard-error');
    if (errorEl) {
      errorEl.hidden = true;
    }
  }

  function refreshFormState(form) {
    const formId = getFormId(form);
    clearHiddenIdIfIncomplete(form, formId);
    if (getUntouchedPositions(formId).length === 0) {
      hideError(form);
    }
  }

  function findFormForInput(input) {
    const formId = input.getAttribute('form');
    return formId ? document.getElementById(formId) : input.closest('form');
  }

  function handleOptionChange(target) {
    if (!(target instanceof Element)) return;
    if (!target.matches('input[data-option-position]')) return;
    const form = findFormForInput(target);
    if (!form) return;
    const formId = getFormId(form);

    getTouched(formId).add(target.getAttribute('data-option-position'));

    // Let theme.js's own change handler run its fetch + swap first,
    // then enforce the untouched-option reset and id clearing.
    setTimeout(() => {
      uncheckUntouchedOptions(formId);
      refreshFormState(form);
    }, 250);
  }

  document.addEventListener('change', (event) => handleOptionChange(event.target));

  // After the variant-picker swaps in fresh HTML following a fetch,
  // re-apply the untouched-option reset (new markup = fresh DOM nodes).
  document.addEventListener('product:rerender', (event) => {
    const form = event.target;
    if (form && form.tagName === 'FORM') {
      const formId = getFormId(form);
      if (event.detail && event.detail.productChange) {
        touchedPositionsByFormId.set(formId, new Set());
      }
      setTimeout(() => {
        uncheckUntouchedOptions(formId);
        refreshFormState(form);
      }, 0);
    }
  });

  // Intercept submit: block + show message if incomplete, otherwise let it proceed
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form.matches('form[action*="/cart/add"]')) return;
      const formId = getFormId(form);
      if (getOptionPositions(formId).length === 0) return; // no options on this product

      if (getUntouchedPositions(formId).length > 0) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showError(form);
        form.querySelector('.variant-guard-error')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } else {
        hideError(form);
      }
    },
    true // capture phase: run before ProductForm's own submit listener
  );
})();